// AGC: Shared mic button for per-field voice input via Web Speech API
import React, { useState } from 'react'
import { Mic, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from '../../../hooks/useTranslation'
import { isMediaPermissionError } from '../../../services/permissionHelper'

interface FieldMicButtonProps {
  /** AGC: Unique ID for toast deduplication */
  fieldName: string
  /** AGC: Called with the raw transcript text */
  onResult: (transcript: string) => void
  /** AGC: Called when mic permission is blocked (show PermissionBlockedModal) */
  onPermissionBlocked?: () => void
  /** AGC: Optional CSS class override */
  className?: string
  /** AGC: Button color scheme — matches parent field theme */
  variant?: 'orange' | 'blue' | 'default'
  /** AGC: Icon size class (default w-4 h-4) */
  iconSize?: string
}

export const FieldMicButton: React.FC<FieldMicButtonProps> = ({
  fieldName,
  onResult,
  onPermissionBlocked,
  className,
  iconSize = 'w-4 h-4',
  variant = 'default',
}) => {
  const { t } = useTranslation()
  const [isListening, setIsListening] = useState(false)

  const variantClasses = {
    orange: 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-300',
    blue: 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300',
    default: 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300',
  }

  const handleMicClick = async () => {
    const toastId = `voice-field-${fieldName}`
    try {
      // AGC: Check for browser support
      const SpeechRecognitionAPI =
        (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
      if (!SpeechRecognitionAPI) {
        toast.error(t('voice.notSupported'))
        return
      }

      // AGC: Request microphone permission first
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (permErr) {
        if (isMediaPermissionError(permErr)) {
          onPermissionBlocked?.()
        } else {
          toast.error(t('voice.micPermissionDenied'))
        }
        return
      }

      const recognition = new SpeechRecognitionAPI()
      recognition.lang = 'pt-BR'
      recognition.continuous = false
      recognition.interimResults = false
      recognition.maxAlternatives = 1

      setIsListening(true)
      toast.loading('🎤 ' + t('voice.speakValue'), { id: toastId })

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript
        onResult(transcript)
        toast.success(`✅ "${transcript}"`, { id: toastId })
        setIsListening(false)
      }

      recognition.onerror = (event: any) => {
        let errorMsg = t('voice.captureError')
        if (event.error === 'not-allowed') {
          errorMsg = t('voice.micPermissionDenied')
        } else if (event.error === 'no-speech') {
          errorMsg = t('voice.noSpeechDetected')
        } else if (event.error === 'aborted') {
          errorMsg = t('voice.voiceCancelled')
        }
        toast.error(errorMsg, { id: toastId })
        setIsListening(false)
      }

      recognition.onend = () => {
        toast.dismiss(toastId)
        setIsListening(false)
      }

      recognition.start()
    } catch (err) {
      toast.error(
        t('voice.captureError'),
        { id: toastId },
      )
      setIsListening(false)
    }
  }

  return (
    <button
      onClick={handleMicClick}
      disabled={isListening}
      className={className || `p-1.5 ${variantClasses[variant]} rounded`}
      title={t('voice.voiceInput')}
    >
      {isListening ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : (
        <Mic className={iconSize} />
      )}
    </button>
  )
}
